import { FILTER_STREAM_STATUS, FILTER_ITEM_CLASS } from '@consts';
import { isValueExist, replaceText } from '@util';
import { distinctUntilChanged } from 'rxjs/operators';
import { combineLatest } from 'rxjs';

const { INIT, UPDATE, COMPLETE } = FILTER_STREAM_STATUS;

export default {
    props: {
        currentStream: {
            type: String,
            default: '',
        },
        keywords: {
            type: String,
            default: '',
        },
        hasTitleTagsInAnyWidgets: {
            type: Boolean,
            default: false,
        },
        isFolderSetting: {
            type: Boolean,
            default: false,
        },
        tagList: {
            type: Array,
            defalt() {
                return [];
            },
        },
        isLegacyChannelFilter: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        currentState() {
            const { values } = this;

            const isStart = R.compose(
                R.where,
                R.fromPairs,
                R.map((key) =>
                    R.equals(key, 'type')
                        ? [key, isValueExist]
                        : [key, R.compose(R.not, isValueExist)]
                ),
                R.keys
            )(values);

            const isComplete = R.compose(
                R.where,
                R.fromPairs,
                R.map((key) => [key, isValueExist]),
                R.keys
            )(values);

            return R.cond([
                [isStart, R.always(INIT)],
                [isComplete, R.always(COMPLETE)],
                [R.defaultTo, R.always(UPDATE)],
            ])(values);
        },
        isActiveStream() {
            const { currentStream, values } = this;
            return currentStream === values.type;
        },
        isAutoComplete() {
            const { keywords, currentStream } = this;
            return keywords && !currentStream;
        },
        shouldHideFilter() {
            const { isActiveStream, isAutoComplete } = this;
            return !isActiveStream && !isAutoComplete;
        },
        prefix() {
            const {
                isAutoComplete,
                values: { type },
                translateOr: tOr,
            } = this;

            return isAutoComplete ? `${tOr(`FILTERS.${type}`, type)}: ` : '';
        },
    },
    methods: {
        observeData$() {
            const {
                generateWatchNew$: gen$,
                emitFilter,
                initToDefaultValues,
            } = this;

            const filterUpdated$ = ['values', 'currentStream'].map((v) =>
                gen$(v)
            );

            const values$ = combineLatest(filterUpdated$).pipe(
                distinctUntilChanged()
            );

            this.$subscribeTo(values$, ([values, nextStream]) => {
                if (!values) return;

                const { isAutoComplete, currentState } = this;

                const isTagCompleted = currentState === COMPLETE;

                const isTagDeleted =
                    nextStream === '' && currentState === UPDATE;

                const streamMatched = nextStream === values.type;

                const tagCompletedOrDeleted = isTagCompleted || isTagDeleted;

                if (streamMatched || isAutoComplete) {
                    emitFilter();
                }

                if (!isAutoComplete && tagCompletedOrDeleted) {
                    initToDefaultValues();
                }
            });
        },
        initToDefaultValues() {
            const { values } = this;

            this.values = {
                ...R.map(R.always(''), values),
                type: values.type,
            };
        },
        getCommonHeader() {
            return !this.isAutoComplete ? (
                <div class="header">
                    {this.$t(`FILTERS.default_option_header`)}
                </div>
            ) : null;
        },
        filterByKeyword(optionList, renderer = null) {
            const {
                keywords,
                values,
                normalizeText,
                translateOr: tOr,
                renderNoOptionFound,
            } = this;
            const { type } = values;

            const filterText = normalizeText(keywords);
            const filterTitle = normalizeText(tOr(`FILTERS.${type}`, type));

            const filteredOptions = optionList.filter((option) => {
                const filterOption = normalizeText(
                    tOr(`FILTERS.${type}_option_${option}`, option)
                );

                return (
                    `${filterTitle}${filterOption}`
                        .toLowerCase()
                        .indexOf(filterText) > -1
                );
            });

            return filteredOptions.length
                ? filteredOptions.map(renderer)
                : renderNoOptionFound();
        },
        highlightMatchingText(text) {
            const { keywords } = this;

            const replacer = replaceText((w) => `<b>${w}</b>`);

            return replacer({ text, keywords });
        },
        normalizeText(text) {
            if (!text) return '';

            return String(text).replace(/\s+/g, '').toLowerCase();
        },
        toggleDropdown(visibility) {
            this.$emit('toggleDropdown', visibility);
        },
        renderNoOptionFound() {
            const { isAutoComplete } = this;

            return isAutoComplete ? null : (
                <li class={`${FILTER_ITEM_CLASS} no-option-found`}>
                    <p>{this.$t(`FILTERS.no_options`)}</p>
                </li>
            );
        },
        getSelectedOption(key = '') {
            return this.tagList.filter(
                ({ type, filterBy }) => type === key && Boolean(filterBy)
            );
        },
    },
    mounted() {
        const { observeData$ } = this;
        observeData$();
    },
};
